import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface HistoryState {
  idx?: number;
}

/**
 * Back and forward for the shell header.
 *
 * The stack is the browser's, so the buttons ask it instead of keeping a second
 * one: React Router writes the entry index into `history.state.idx`, and
 * `idx > 0` is exactly "there is an entry behind this one". A tab that has only
 * ever shown one page has no back entry, which is why the button starts disabled
 * instead of appearing to work and doing nothing.
 *
 * Forward has no platform equivalent — nothing answers "are there entries ahead
 * of me?" — so the furthest entry reached this session is remembered here:
 * forward is offered while the reader is behind it, and stepping back into it
 * spends it, like a browser.
 */
export function useHistoryNav() {
  const navigate = useNavigate();
  // A subscription, so the buttons re-render when the browser's stack moves.
  useLocation();
  const idx = readIndex();

  const [deepest, setDeepest] = useState(idx);
  // Adjusting state during render — React's own pattern for remembering
  // something from the last render — so the forward button is right on the pass
  // that follows the navigation, not one paint later.
  if (idx > deepest) setDeepest(idx);

  return {
    canGoBack: idx > 0,
    canGoForward: idx < deepest,
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
  };
}

/**
 * The browser's entry index for the page on screen. A MemoryRouter (the tests)
 * keeps no browser history and a freshly opened tab has none either, so this
 * reads 0 in both — which is what the buttons should say.
 */
function readIndex(): number {
  if (typeof window === 'undefined') return 0;
  const state = window.history.state as HistoryState | null;
  return typeof state?.idx === 'number' ? state.idx : 0;
}
